/**
 * useItemListState — manages all view state for the item list.
 * Search, filters, sort, grouping, column visibility, density mode.
 * URL state via useSearchParams for shareability.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { fieldsInSpot, placementOf } from '@/lib/customFieldPlacement';
import { useSearchParams } from 'react-router-dom';
import { useAttributesStore } from '../../../../stores/prioritization/attributesStore';
import {
  ITEM_FIELD_BY_KEY,
  COLUMN_ORDER,
  DEFAULT_VISIBLE_ORDER,
} from '../config/itemFields';
import { IMPORT_BATCH_PARAM } from '../../../../lib/importBatch';
import { isPipelineBucket, PIPELINE_BUCKET_LABEL } from '../../../../lib/pipelineHealth';
import { MOBILE_MEDIA_QUERY } from '../../../../mobile/featureFlag';

const IMPORT_BATCH_GROUP_ID = 'preset-import-batch';

function isPhoneWidth(): boolean {
  try { return window.matchMedia(MOBILE_MEDIA_QUERY).matches; } catch { return false; }
}

// ============================================================================
// Types
// ============================================================================

export type DensityMode = 'compact' | 'default' | 'comfortable';
export type LayoutMode = 'table' | 'card';

export type FilterOperator =
  | 'in' | 'eq' | 'range' | 'boolean' | 'text' | 'date-range'
  // PQL statement operators (evaluated by src/lib/pql/evaluate.ts; the panel
  // doesn't author these, but displays and removes them like any rule):
  | 'empty' // IS [NOT] EMPTY
  | 'gt' | 'lt' // strict numeric comparison — only matches items that HAVE a value
  | 'date-after' | 'date-before'; // strictly outside the given day — only matches items with a date

export interface FilterRule {
  id: string;
  field: string;
  label: string;
  operator: FilterOperator;
  /** date-range uses [isoStart, isoEnd] where empty string means unbounded */
  value: string | string[] | boolean | number | [number, number] | [string, string];
  displayValue: string;
}

/** Backward-compat alias */
export type ActiveFilter = FilterRule;

/**
 * A group of rules joined by a conjunction.
 * Multiple groups are always ANDed together.
 * e.g. (Category=Problem OR Risk) AND (Status=Raw) AND (Has Cluster=Yes)
 */
export interface FilterGroup {
  id: string;
  conjunction: 'AND' | 'OR';
  rules: FilterRule[];
}

export interface SortSpec {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ItemListState {
  searchQuery: string;
  filters: ActiveFilter[];
  sort: SortSpec[];
  groupBy: string | null;
  density: DensityMode;
  expandedGroups: Set<string>;
}

// ============================================================================
// Default column set
// ============================================================================

// Derived from the itemFields registry (config/itemFields.ts) — the single source
// of truth for built-in fields. Column order comes from COLUMN_ORDER; the default
// visible set from DEFAULT_VISIBLE_ORDER (independent of each column's
// `defaultVisible` flag). Adding a column is a single registry entry.

export const DEFAULT_VISIBLE_COLUMNS = DEFAULT_VISIBLE_ORDER;

// Saved column layouts. The key moves when the default set changes, so everyone
// starts from the new defaults (v2, 2026-10-15: Relationships on; Quality and Status off).
const ITEM_LIST_COLUMNS_KEY = 'itemListVisibleColumns.v2';

export const ALL_COLUMNS = COLUMN_ORDER.map((key) => {
  const c = ITEM_FIELD_BY_KEY[key].column!;
  return { key, label: c.pickerLabel ?? c.label, defaultVisible: c.defaultVisible };
});

/**
 * Returns the full column list: static built-in columns + any enabled custom
 * fields (stored in items.metadata). Custom field column keys use the prefix
 * `meta:FIELD_KEY` to distinguish them from built-in columns.
 */
export function useAllColumns() {
  const attributes = useAttributesStore((s) => s.attributes);
  const customFieldCols = useMemo(
    () =>
      attributes
        .filter(
          (a) =>
            a.isCustomField === true &&
            a.enabled !== false &&
            (a.objectType ?? 'item') === 'item'
        )
        .map((a) => ({
          key: `meta:${a.key}`,
          label: a.label,
          // Settings → Custom Fields → Shows on → Item list column.
          defaultVisible: placementOf(a).listColumn,
        })),
    [attributes]
  );
  return useMemo(() => [...ALL_COLUMNS, ...customFieldCols], [customFieldCols]);
}

// ============================================================================
// Default filter — Status = Active (hides archived/deferred by default)
// ============================================================================

const DEFAULT_STATUS_FILTER_GROUP: FilterGroup = {
  id: 'default-status',
  conjunction: 'AND',
  rules: [
    {
      id: 'default-status-rule',
      field: 'exit_status',
      label: 'Status',
      operator: 'in',
      value: ['active'],
      displayValue: 'Active',
    },
  ],
};

// ============================================================================
// The filters the item list opens with
// ============================================================================

/**
 * The item list's opening filters: Status = Active, plus whatever the address
 * seeds — a pipeline stage (the Pipeline Health funnel), stale items, a
 * category, an altitude, a track. "Clear" goes back to Status = Active alone.
 * A new report opens with the same filters, printed as its statement.
 */
export function itemListOpeningFilters(search: string = window.location.search): FilterGroup[] {
  const params = new URLSearchParams(search);

  // Seed a pipeline stage from the URL (the Pipeline Health funnel opens the
  // list this way). An exit (deferred / archived) replaces the default
  // Status=Active rule — otherwise the two would cancel and show nothing.
  const stage = params.get('stage');
  const stageGroups: FilterGroup[] = [];
  if (isPipelineBucket(stage)) {
    if (stage === 'deferred' || stage === 'archived') {
      stageGroups.push({
        id: 'default-status',
        conjunction: 'AND',
        rules: [{
          id: 'default-status-rule',
          field: 'exit_status',
          label: 'Status',
          operator: 'in',
          value: [stage],
          displayValue: PIPELINE_BUCKET_LABEL[stage],
        }],
      });
    } else {
      stageGroups.push(DEFAULT_STATUS_FILTER_GROUP, {
        id: 'preset-pipeline-stage',
        conjunction: 'AND',
        rules: [{
          id: 'preset-pipeline-stage-rule',
          field: 'pipelineStage',
          label: 'Pipeline stage',
          operator: 'in',
          value: [stage],
          displayValue: PIPELINE_BUCKET_LABEL[stage],
        }],
      });
    }
  }
  const groups: FilterGroup[] = stageGroups.length > 0 ? stageGroups : [DEFAULT_STATUS_FILTER_GROUP];

  // ?stale=1 — the Pipeline Health age chart opens the items past 30 days here.
  if (params.get('stale') === '1') {
    groups.push({
      id: 'preset-stale',
      conjunction: 'AND',
      rules: [{ id: 'preset-stale-rule', field: 'isStale', label: 'Stale', operator: 'boolean', value: true, displayValue: 'Yes' }],
    });
  }

  // Seed category + altitude filters from URL (e.g. when navigating from the Track Cells page)
  const category = params.get('category');
  const altitude = params.get('altitude') ?? params.get('altitudeLevel');

  const presetRules: FilterRule[] = [];
  if (category) {
    presetRules.push({
      id: 'preset-category',
      field: 'itemCategory',
      label: 'Category',
      operator: 'in',
      value: [category],
      displayValue: category.charAt(0).toUpperCase() + category.slice(1),
    });
  }
  if (altitude) {
    presetRules.push({
      id: 'preset-altitude',
      field: 'altitude',
      label: 'Altitude',
      operator: 'in',
      value: [String(altitude)],
      displayValue: `Level ${altitude}`,
    });
  }
  // Seed Track filter from URL (e.g. when navigating from item detail)
  const trackId = params.get('trackId');
  const trackName = params.get('trackName');
  if (trackId) {
    presetRules.push({
      id: 'preset-track',
      field: 'trackId',
      label: 'Track',
      operator: 'in',
      value: [trackId],
      displayValue: trackName ? decodeURIComponent(trackName) : trackId,
    });
  }
  if (presetRules.length > 0) {
    groups.push({ id: 'preset-track-cell', conjunction: 'AND', rules: presetRules });
  }

  return groups;
}

// ============================================================================
// Hook
// ============================================================================

function getStoredDensity(): DensityMode {
  try {
    const stored = localStorage.getItem('itemListDensity');
    if (stored === 'compact' || stored === 'default' || stored === 'comfortable') return stored;
  } catch { /* noop */ }
  return 'default';
}

function getStoredColumnWidths(): Record<string, number> {
  try {
    const raw = localStorage.getItem('itemListColumnWidths');
    if (raw) return JSON.parse(raw) as Record<string, number>;
  } catch { /* noop */ }
  return {};
}

export interface ItemListStateOptions {
  /**
   * The filters the list opens with and goes back to on "Clear". Default: the
   * item list's — Status = Active, plus whatever the address seeds (a pipeline
   * stage, a category, a track…). The item pool opens with none: every member.
   */
  initialFilters?: FilterGroup[];
  /**
   * Where the person's column choice is kept in this browser, and the set
   * shown before they choose. Default: the item list's.
   */
  columns?: { storageKey: string; defaults: string[] };
}

export function useItemListState(options: ItemListStateOptions = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once: the list's opening filters, which "Clear" returns to.
  const initialFiltersRef = useRef(options.initialFilters);
  const columnsKey = options.columns?.storageKey ?? ITEM_LIST_COLUMNS_KEY;
  const ownDefaultColumns = options.columns?.defaults;

  // Search from URL
  const searchQuery = searchParams.get('q') || '';
  const setSearchQuery = useCallback(
    (q: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (q) next.set('q', q);
        else next.delete('q');
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Sort from URL
  const sort: SortSpec[] = useMemo(() => {
    const raw = searchParams.get('sort');
    if (!raw) return [{ field: 'updatedAt', direction: 'desc' as const }];
    return raw.split(',').map((s) => {
      const [field, dir] = s.split(':');
      return { field, direction: (dir === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc' };
    });
  }, [searchParams]);

  const setSort = useCallback(
    (specs: SortSpec[]) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (specs.length === 0 || (specs.length === 1 && specs[0].field === 'updatedAt' && specs[0].direction === 'desc')) {
          next.delete('sort');
        } else {
          next.set('sort', specs.map((s) => `${s.field}:${s.direction}`).join(','));
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Group from URL
  const groupBy = searchParams.get('group') || null;
  const setGroupBy = useCallback(
    (field: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (field) next.set('group', field);
        else next.delete('group');
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Density mode (persisted in localStorage)
  const [density, setDensityInternal] = useState<DensityMode>(getStoredDensity);
  const setDensity = useCallback((mode: DensityMode) => {
    setDensityInternal(mode);
    try { localStorage.setItem('itemListDensity', mode); } catch { /* noop */ }
  }, []);

  // Filter groups — local state (seeded from URL params if present, otherwise default Status=Active)
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(
    () => initialFiltersRef.current ?? itemListOpeningFilters(),
  );

  // One import run from the URL (import done screen → "View these N items").
  // Items carry the run's id in items.import_batch_id (hydrated as
  // importBatchId). An effect, not the initializer, because the import modal
  // usually opens from this same list — the page doesn't remount when the
  // param arrives, so the filter has to follow the URL.
  const importBatchParam = searchParams.get(IMPORT_BATCH_PARAM);
  useEffect(() => {
    setFilterGroups((prev) => {
      const without = prev.filter((g) => g.id !== IMPORT_BATCH_GROUP_ID);
      if (!importBatchParam) return without.length === prev.length ? prev : without;
      return [
        ...without,
        {
          id: IMPORT_BATCH_GROUP_ID,
          conjunction: 'AND',
          rules: [
            {
              id: 'preset-import-batch',
              field: 'importBatchId',
              label: 'Import',
              operator: 'in',
              value: [importBatchParam],
              displayValue: 'This import',
            },
          ],
        },
      ];
    });
  }, [importBatchParam]);

  const addGroup = useCallback((conjunction: 'AND' | 'OR' = 'AND'): string => {
    const id = crypto.randomUUID();
    setFilterGroups((prev) => [...prev, { id, conjunction, rules: [] }]);
    return id;
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setFilterGroups((prev) => prev.filter((g) => g.id !== groupId));
  }, []);

  const setGroupConjunction = useCallback((groupId: string, conjunction: 'AND' | 'OR') => {
    setFilterGroups((prev) =>
      prev.map((g) => (g.id === groupId ? { ...g, conjunction } : g))
    );
  }, []);

  const addRule = useCallback((rule: Omit<FilterRule, 'id'>, groupId?: string) => {
    const newRule: FilterRule = { ...rule, id: crypto.randomUUID() };
    setFilterGroups((prev) => {
      if (groupId) {
        return prev.map((g) => g.id === groupId ? { ...g, rules: [...g.rules, newRule] } : g);
      }
      // Append to last group, or create a new group
      if (prev.length === 0) {
        return [{ id: crypto.randomUUID(), conjunction: 'AND', rules: [newRule] }];
      }
      return prev.map((g, i) => i === prev.length - 1 ? { ...g, rules: [...g.rules, newRule] } : g);
    });
  }, []);

  const removeRule = useCallback((groupId: string, ruleId: string) => {
    setFilterGroups((prev) =>
      prev
        .map((g) => g.id === groupId ? { ...g, rules: g.rules.filter((r) => r.id !== ruleId) } : g)
        .filter((g) => g.rules.length > 0)
    );
  }, []);

  const clearFilters = useCallback(
    () => setFilterGroups(initialFiltersRef.current ?? [DEFAULT_STATUS_FILTER_GROUP]),
    [],
  );

  /** Replace the whole filter state at once (PQL statement → builder sync). */
  const replaceFilters = useCallback((groups: FilterGroup[]) => {
    setFilterGroups(groups.filter((g) => g.rules.length > 0));
  }, []);

  // Total rule count across all groups (for toolbar badge)
  const filterCount = filterGroups.reduce((sum, g) => sum + g.rules.length, 0);

  // Column visibility and order — persisted in localStorage. With nothing
  // saved, the default is the built-in set plus every custom field whose
  // Shows on includes "Item list column"; that default follows the setting.
  const listAttributes = useAttributesStore((s) => s.attributes);
  const defaultColumns = useMemo(
    () => ownDefaultColumns ?? [...DEFAULT_VISIBLE_COLUMNS, ...fieldsInSpot(listAttributes, 'listColumn').map((a) => `meta:${a.key}`)],
    [listAttributes, ownDefaultColumns]
  );
  const [savedColumns, setSavedColumns] = useState<string[] | null>(() => {
    try {
      const stored = localStorage.getItem(columnsKey);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        // Validate: keep built-in keys + custom field keys (meta:*)
        const validKeys = new Set(ALL_COLUMNS.map((c) => c.key));
        const filtered = parsed.filter((k) => validKeys.has(k) || k.startsWith('meta:'));
        if (filtered.length > 0) {
          // Insert Authorship after Title for saved layouts that predate the column.
          if (!filtered.includes('creationProvenance')) {
            const titleIdx = filtered.indexOf('title');
            const insertAt = titleIdx >= 0 ? titleIdx + 1 : 0;
            filtered.splice(insertAt, 0, 'creationProvenance');
          }
          return filtered;
        }
      }
    } catch { /* noop */ }
    return null;
  });
  const visibleColumns = savedColumns ?? defaultColumns;
  const saveColumns = useCallback((cols: string[]) => {
    setSavedColumns(cols);
    try { localStorage.setItem(columnsKey, JSON.stringify(cols)); } catch { /* noop */ }
  }, [columnsKey]);
  const setVisibleColumns = saveColumns;
  const toggleColumn = useCallback(
    (key: string) => {
      const prev = savedColumns ?? defaultColumns;
      saveColumns(prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
    },
    [savedColumns, defaultColumns, saveColumns]
  );
  const reorderColumns = saveColumns;

  // Expanded groups
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Expanded clusters — a settled cluster shows as one entry until expanded
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());
  const toggleCluster = useCallback((groupId: string) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  // Layout mode — table or card (persisted in localStorage). Until someone
  // chooses, a phone starts on cards: the table is a wide grid that shows one
  // column at 390px, and the cards carry the title, status and altitude.
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>(() => {
    const fallback: LayoutMode = isPhoneWidth() ? 'card' : 'table';
    try { return (localStorage.getItem('itemListLayoutMode') as LayoutMode) ?? fallback; } catch { return fallback; }
  });
  const setLayoutMode = useCallback((mode: LayoutMode) => {
    setLayoutModeState(mode);
    try { localStorage.setItem('itemListLayoutMode', mode); } catch { /* noop */ }
  }, []);

  // Column widths (persisted in localStorage)
  const [columnWidths, setColumnWidthsState] = useState<Record<string, number>>(getStoredColumnWidths);
  const setColumnWidth = useCallback((key: string, width: number) => {
    setColumnWidthsState((prev) => {
      const next = { ...prev, [key]: width };
      try { localStorage.setItem('itemListColumnWidths', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);
  const resetColumnWidth = useCallback((key: string) => {
    setColumnWidthsState((prev) => {
      const next = { ...prev };
      delete next[key];
      try { localStorage.setItem('itemListColumnWidths', JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  // Row selection
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const toggleRowSelection = useCallback((id: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const selectAllRows = useCallback((ids: string[]) => {
    setSelectedRows(new Set(ids));
  }, []);
  /** Select or deselect several ids at once (a cluster entry's members). */
  const setRowsSelected = useCallback((ids: string[], selected: boolean) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (selected) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedRows(new Set()), []);

  return {
    searchQuery,
    setSearchQuery,
    sort,
    setSort,
    groupBy,
    setGroupBy,
    density,
    setDensity,
    filterGroups,
    filterCount,
    addGroup,
    removeGroup,
    setGroupConjunction,
    addRule,
    removeRule,
    clearFilters,
    replaceFilters,
    visibleColumns,
    setVisibleColumns,
    toggleColumn,
    reorderColumns,
    expandedGroups,
    toggleGroup,
    expandedClusters,
    toggleCluster,
    selectedRows,
    toggleRowSelection,
    selectAllRows,
    setRowsSelected,
    clearSelection,
    columnWidths,
    setColumnWidth,
    resetColumnWidth,
    layoutMode,
    setLayoutMode,
  };
}
